import { Fragment, useId, useMemo, useState } from "react";

import {
  ButtonBase,
  Chip,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  ErrorOutlineRounded,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";

import { ChevronRightIcon } from "igfd-ui/src";

import {
  type MenuTreeNode,
  isValidGuestGroupHeader,
} from "../../menu/menu-tree";
import {
  type OptionVerdict,
  allergenBands,
  sortOptionsBySuitability,
  verdictForSelection,
} from "../../menu/staff-allergen-bands";
import { staffMenuCopy } from "../../menu/staff-menu-copy";
import {
  INDENT_STEP,
  MAX_STAFF_DEPTH,
  countChoiceGroups,
  countOptions,
  dishChoiceGroups,
} from "../../menu/staff-menu-tree";

/**
 * One choice group inside a dish, and the options under it.
 *
 * Groups open in place rather than opening a sheet. The guest menu makes a
 * nested group a tap because a guest comparing options loses the thread; a
 * waiter is reading out what is in something, so the whole dish stays one
 * scroll with no navigation state to lose. It also keeps the app to its two
 * existing layers, dish over list and product over dish.
 *
 * A group whose own record cannot be read keeps a row and loses its chevron,
 * because there is nothing behind it to open. An option the response withheld
 * is a different thing and renders nothing: it is hidden on purpose and cannot
 * be served.
 *
 * An option row carries its name and nothing else until a waiter selects an
 * allergen above. Listing every option's allergens made a five-option group a
 * wall of small print that still had to be read line by line and compared by
 * hand, which is the work the screen is supposed to do.
 */
interface ChoiceGroupProps {
  node: MenuTreeNode;
  depth: number;
  /** What the waiter selected in the bands above. Empty means no question yet. */
  selectedAllergenIds: string[];
  /** True when this group is why the drawer is open, so it starts expanded. */
  defaultOpen?: boolean;
  /** First row in its container, which draws its own top edge. */
  isFirst?: boolean;
  /**
   * True when the drawer's own title already names this group. Repeating it on
   * a row directly underneath says the same thing twice and gives the waiter a
   * control that collapses the only thing on the screen.
   */
  hideHeader?: boolean;
  /** The group title travels with the option so the drawer can name where it
   * came from. A node has no parent pointer to recover it from. */
  onOpenOption: (node: MenuTreeNode, groupTitle: string) => void;
}

const rowPadding = (depth: number) => ({
  paddingLeft: 2 + Math.min(depth, MAX_STAFF_DEPTH) * INDENT_STEP,
  paddingRight: 2,
  paddingY: 2,
});

const UnreadableRow = ({
  depth,
  title,
  subtitle,
}: {
  depth: number;
  title: string;
  subtitle?: string;
}) => {
  const {
    palette: { oscar },
  } = useTheme();

  return (
    <Stack
      alignItems="flex-start"
      direction="row"
      gap={1}
      sx={{
        ...rowPadding(depth),
        borderTop: `1px solid ${oscar.light.borders.gray}`,
        width: "100%",
      }}
    >
      <ErrorOutlineRounded
        fontSize="small"
        sx={{ color: oscar.light.content.danger }}
      />
      <Stack gap={0.5} minWidth={0}>
        <Typography
          variant="labelRMedium"
          color={oscar.light.content.dangerText}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="labelS" color={oscar.light.content.content1}>
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

/**
 * The verdict in words, because it is the thing a waiter is about to say to the
 * table. "Can be removed" and "Not suitable" were one label until they were
 * not: one ends the conversation, the other is an order note.
 */
const VerdictMark = ({ verdict }: { verdict: OptionVerdict }) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();

  const tone = {
    suitable: {
      label: staffMenuCopy.optionSuitable,
      background: oscar.light.surface.safe,
      color: oscar.light.content.accentText,
    },
    // An order note, not a refusal, so it does not take the alarm colour.
    "can-be-removed": {
      label: staffMenuCopy.optionCanBeRemoved,
      background: oscar.light.surface.takeNote,
      color: oscar.light.content.takeNote,
    },
    // Also a yes, and the same colour, but the waiter has to open this one
    // rather than write a note on the ticket.
    "depends-on-choice": {
      label: staffMenuCopy.optionDependsOnChoice,
      background: oscar.light.surface.takeNote,
      color: oscar.light.content.takeNote,
    },
    "not-suitable": {
      label: staffMenuCopy.optionNotSuitable,
      background: oscar.light.surface.danger,
      color: oscar.light.content.dangerText,
    },
    "may-contain": {
      label: staffMenuCopy.optionMayContain,
      background: oscar.light.surface.warning,
      color: oscar.light.content.content1,
    },
  }[verdict];

  return (
    <Chip
      label={tone.label}
      size="small"
      sx={{
        backgroundColor: tone.background,
        color: tone.color,
        flexShrink: 0,
        ...typography.labelSMedium,
      }}
    />
  );
};

const OptionRow = ({
  node,
  depth,
  verdict,
  onOpen,
}: {
  node: MenuTreeNode;
  depth: number;
  /** Worked out once by the group, which needs the same answer for its count. */
  verdict: OptionVerdict | null;
  onOpen: (node: MenuTreeNode) => void;
}) => {
  const {
    palette: { oscar },
  } = useTheme();

  // Hidden or unverified. Withheld by the response on purpose, so it is not an
  // option and says nothing.
  if (!node.dish) return null;

  return (
    <ButtonBase
      type="button"
      onClick={() => onOpen(node)}
      aria-label={
        verdict
          ? `${node.dish.name}. ${
              {
                suitable: staffMenuCopy.optionSuitable,
                "can-be-removed": staffMenuCopy.optionCanBeRemoved,
                "depends-on-choice": staffMenuCopy.optionDependsOnChoice,
                "not-suitable": staffMenuCopy.optionNotSuitable,
                "may-contain": staffMenuCopy.optionMayContain,
              }[verdict]
            }`
          : node.dish.name
      }
      sx={{
        ...rowPadding(depth),
        alignItems: "stretch",
        borderTop: `1px solid ${oscar.light.borders.gray}`,
        textAlign: "left",
        width: "100%",
        "&:focus-visible": {
          outline: `3px solid ${oscar.light.icon.link}`,
          outlineOffset: "-3px",
        },
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        gap={2}
        justifyContent="space-between"
        width="100%"
      >
        <Typography variant="labelLMedium">{node.dish.name}</Typography>
        <Stack alignItems="center" direction="row" gap={1} flexShrink={0}>
          {verdict ? <VerdictMark verdict={verdict} /> : null}
          <ChevronRightIcon
            height={16}
            width={16}
            color={oscar.light.content.link}
          />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

export const ChoiceGroup = ({
  node,
  depth,
  selectedAllergenIds,
  defaultOpen = false,
  isFirst = false,
  hideHeader = false,
  onOpenOption,
}: ChoiceGroupProps) => {
  const theme = useTheme();
  const {
    palette: { oscar },
  } = theme;
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  // One subtree walk per option, shared by the count, the sort and the rows.
  // Each of those used to call `allergenBands` itself, so a ten-option group
  // did thirty full walks on every allergen tick and every expand.
  const verdicts = useMemo(() => {
    const byId = new Map<string, OptionVerdict>();
    if (selectedAllergenIds.length === 0) return byId;
    for (const child of node.children) {
      if (child.kind !== "dish" || !child.dish) continue;
      byId.set(
        child.id,
        verdictForSelection(allergenBands(child), selectedAllergenIds)
      );
    }
    return byId;
  }, [node, selectedAllergenIds]);

  const header = isValidGuestGroupHeader(node.header) ? node.header : null;
  if (!header) {
    return (
      <UnreadableRow depth={depth} title={staffMenuCopy.groupUnreadableTitle} />
    );
  }

  const options = countOptions(node);
  const nestedGroups = countChoiceGroups(node);
  // Only counted against the options this group offers directly. A group of
  // groups has none, so there is no ratio to state and its sub-groups carry
  // their own.
  const suitable =
    selectedAllergenIds.length && options > 0
      ? [...verdicts.values()].filter((verdict) => verdict === "suitable")
          .length
      : null;
  const meta =
    suitable === null
      ? staffMenuCopy.groupMeta(header.required, options, nestedGroups)
      : `${staffMenuCopy.groupMeta(
          header.required,
          options,
          nestedGroups
        )} · ${staffMenuCopy.optionsSuitable(suitable, options)}`;

  return (
    <Stack width="100%">
      {hideHeader ? null : (
        <ButtonBase
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-controls={contentId}
          // The meta line sits inside this button, where aria-label would silence
          // it, so it is spoken here from the same helper the eye reads.
          aria-label={`${header.title}. ${meta}`}
          sx={{
            ...rowPadding(depth),
            alignItems: "stretch",
            // Sits on the section grey already; a fill here only doubled it,
            // so the rule between groups is the only thing separating them.
            // Told rather than guessed from position: this button is the only
            // one inside its own wrapper, so `:first-of-type` matched every
            // group and quietly removed all of them.
            borderTop: isFirst
              ? "none"
              : `1px solid ${oscar.light.borders.gray}`,
            textAlign: "left",
            width: "100%",
            "&:focus-visible": {
              outline: `3px solid ${oscar.light.icon.link}`,
              outlineOffset: "-3px",
            },
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            gap={2}
            justifyContent="space-between"
            width="100%"
          >
            <Stack alignItems="flex-start" gap={0.5} minWidth={0}>
              <Typography variant="labelLHeavy">{header.title}</Typography>
              <Typography
                variant="labelSMedium"
                color={oscar.light.content.content1}
              >
                {meta}
              </Typography>
            </Stack>
            <KeyboardArrowDownRounded
              sx={{
                color: oscar.light.icon.dark,
                flexShrink: 0,
                transform: open ? "rotate(180deg)" : "none",
                transition: prefersReducedMotion
                  ? "none"
                  : theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shorter,
                    }),
              }}
            />
          </Stack>
        </ButtonBase>
      )}

      <Collapse
        in={hideHeader || open}
        id={contentId}
        timeout={prefersReducedMotion ? 0 : "auto"}
        unmountOnExit
      >
        {sortOptionsBySuitability(
          node.children,
          selectedAllergenIds,
          verdicts
        ).map((child, index) =>
          child.kind === "dish" ? (
            <Fragment key={child.id}>
              <OptionRow
                node={child}
                depth={depth + 1}
                verdict={verdicts.get(child.id) ?? null}
                onOpen={(option) => onOpenOption(option, header.title)}
              />
              {/* An option can own groups of its own: pick a sauce, then its
                  heat. They belong under the option that asks for them. */}
              {dishChoiceGroups(child).map((grandchild) => (
                <ChoiceGroup
                  key={grandchild.id}
                  node={grandchild}
                  depth={depth + 2}
                  selectedAllergenIds={selectedAllergenIds}
                  onOpenOption={onOpenOption}
                />
              ))}
            </Fragment>
          ) : (
            <ChoiceGroup
              key={child.id}
              node={child}
              depth={depth + 1}
              // With this group's own header hidden, its first child is the
              // first row in the card and has no sibling to be ruled off.
              isFirst={hideHeader && index === 0}
              selectedAllergenIds={selectedAllergenIds}
              onOpenOption={onOpenOption}
            />
          )
        )}
      </Collapse>
    </Stack>
  );
};
